import { useCallback, useRef, useState } from 'react';

import type { FileUIPart } from 'ai';
import { useSnackbar } from 'notistack';

import { MAX_COMPOSER_IMAGES } from '../imageAttachments';

import { imagePartFromFile } from './imageParts';

export interface ImageAttachments {
  attachments: Array<FileUIPart>;
  reading: boolean;
  add: (files: ReadonlyArray<File>) => void;
  remove: (index: number) => void;
  replace: (next: Array<FileUIPart>) => void;
}

export function useImageAttachments(): ImageAttachments {
  const { enqueueSnackbar } = useSnackbar();
  const [attachments, setAttachments] = useState<Array<FileUIPart>>([]);
  const [readingCount, setReadingCount] = useState(0);
  const countRef = useRef(0);
  const generationRef = useRef(0);

  const replace = useCallback((next: Array<FileUIPart>) => {
    generationRef.current += 1;
    countRef.current = next.length;
    setReadingCount(0);
    setAttachments(next);
  }, []);

  const add = useCallback(
    (files: ReadonlyArray<File>) => {
      const images = files.filter((file) => file.type.startsWith('image/'));
      const freeSlots = MAX_COMPOSER_IMAGES - countRef.current;
      if (images.length === 0) {
        return;
      }
      if (freeSlots <= 0) {
        enqueueSnackbar(`You can add up to ${MAX_COMPOSER_IMAGES} images.`, { variant: 'warning' });
        return;
      }
      if (images.length > freeSlots) {
        enqueueSnackbar(`Only the first ${freeSlots} images were added.`, { variant: 'warning' });
      }
      const accepted = images.slice(0, freeSlots);
      const generation = generationRef.current;
      countRef.current += accepted.length;
      setReadingCount((count) => count + accepted.length);
      accepted.forEach((file) => {
        imagePartFromFile(file).then(
          (part) => {
            if (generation !== generationRef.current) {
              return;
            }
            setReadingCount((count) => count - 1);
            setAttachments((current) => [...current, part]);
          },
          (error: unknown) => {
            if (generation !== generationRef.current) {
              return;
            }
            setReadingCount((count) => count - 1);
            countRef.current -= 1;
            enqueueSnackbar(error instanceof Error ? error.message : 'That image could not be read.', {
              variant: 'error',
            });
          },
        );
      });
    },
    [enqueueSnackbar],
  );

  const remove = useCallback((index: number) => {
    countRef.current -= 1;
    setAttachments((current) => current.filter((_, position) => position !== index));
  }, []);

  return { attachments, reading: readingCount > 0, add, remove, replace };
}
